import { useCallback, useMemo, useRef, useState } from 'react';
import type { ExpandedState } from '@tanstack/react-table';
import { useQueryStates, parseAsString } from 'nuqs';
import {
  Button,
  DataTable,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  InlineStack,
  Page,
  PageBody,
  PageHeader,
  PageHeaderIconTile,
  PageHeaderTitle,
  Paragraph,
  Select,
  TabNav,
  TabNavList,
  TabNavTab,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { useAuth } from '@/services/auth';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { PlanFeature } from '@/services/auth/types';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { AutoRefreshControl } from '@/components/auto-refresh-control';
import { useAutoRefresh } from '@/features/user-flags';
import { useListWorkspaceObjectTypes, type WorkspaceObjectType } from '@/features/workspaces';
import { useActivityLogEnvironment, useLogs } from '@/services/db/activity-log/queries';
import { createAnonymousColumns, createIdentifiedColumns } from './components/columns';
import { ExpandedRow } from './components/expanded-row';
import { DateRangeFilter } from './components/date-range-filter';
import { NameFilter } from './components/name-filter';
import { activityLogTypeLabel, buildActivityTypeOptions, type ActivityLogTypeTerms } from './activity-log-types';
import { useActivityLogsAnalytics } from './analytics';
import { filtersForTypeChange } from './filters';
import type { IdentifierConfig } from './display-name';
import type { ActivityLogRow, ActivityLogSegment, ActivityLogTab } from './types';

interface ActivityLogsPageProps {
  /** Workspace id from the route == API environment_id. */
  workspaceId: string;
  tab: ActivityLogTab;
  /**
   * Href for each tab (the route owns the URL). Rendered as real links via Pluma `TabNav`, which
   * routes these internal hrefs through the app router — so switching tabs is a client-side
   * navigation that preserves the current filters (search params), no click handler needed.
   */
  tabHref: (tab: ActivityLogTab) => string;
}

const filterParsers = {
  type: parseAsString.withDefault(''),
  name: parseAsString.withDefault(''),
  // Date-range params are preserved for deep links / the redirect even though the
  // date-range UI is gated behind the `activity-logs-time-range` flag (deferred).
  from: parseAsString.withDefault(''),
  to: parseAsString.withDefault(''),
};

/**
 * The Anonymous tab hides the filter bar, so the Identified-tab filters (preserved in the URL when
 * you switch tabs) are invisible and unclearable there. Don't send them to the API — the URL keeps
 * them, so switching back to Identified restores them.
 */
const ANONYMOUS_FILTERS = { type: '', name: '', from: '', to: '' };

/** Show the table's loading state on the first load (and while a fresh fetch hasn't returned rows). */
function isInitialTableLoad(isLoading: boolean, isFetching: boolean, rowCount: number): boolean {
  return isLoading || (isFetching && rowCount === 0);
}

function NoLogsState() {
  return (
    <EmptyState>
      <EmptyStateTitle>No data available</EmptyStateTitle>
    </EmptyState>
  );
}

function LogsErrorState() {
  return (
    <EmptyState>
      <EmptyStateTitle>Something went wrong</EmptyStateTitle>
      <EmptyStateDescription>Please refresh the page and try again.</EmptyStateDescription>
    </EmptyState>
  );
}

/** Table empty-slot component: the error state on a failed fetch, otherwise the "no data" state. */
function emptyStateForFetch(isError: boolean) {
  return isError ? LogsErrorState : NoLogsState;
}

type ActivityLogFilters = { type: string; name: string; from: string; to: string };

type LogsToolbarProps = {
  isAnonymous: boolean;
  /** Workspace id == API environment_id, for the webhook name filter's campaign lookups. */
  environmentId: string;
  filters: ActivityLogFilters;
  setFilters: (values: Partial<ActivityLogFilters>) => unknown;
  activityTypeOptions: ReturnType<typeof buildActivityTypeOptions>;
  sentEvents: string[];
  sentAttributes: string[];
  lastUpdated: number;
  autoRefresh: boolean;
  onAutoRefreshChange: (next: boolean) => void;
};

/** The filter controls (left) + last-updated / auto-refresh status (right) above the table. */
function LogsToolbar({
  isAnonymous,
  environmentId,
  filters,
  setFilters,
  activityTypeOptions,
  sentEvents,
  sentAttributes,
  lastUpdated,
  autoRefresh,
  onAutoRefreshChange,
}: LogsToolbarProps) {
  const hasFilter =
    !isEmptyString(filters.type) ||
    !isEmptyString(filters.name) ||
    !isEmptyString(filters.from) ||
    !isEmptyString(filters.to);

  return (
    <InlineStack alignInline="space-between" alignBlock="flex-end" mt="200" mb="100">
      {/* Bottom-align: the Activity Type Select carries a label above its input, so the
          label-less controls (name filter, date range, Clear Filter) sit on the input's
          baseline rather than floating up to the label row. InlineStack's
          cross-axis prop is `alignBlock` (not `alignItems`, which it silently ignores). */}
      <InlineStack alignBlock="flex-end" gap="100">
        {isAnonymous ? (
          <Paragraph>Events for last 24 hours.</Paragraph>
        ) : (
          <>
            <Select
              label="Activity Type"
              placeholder="Select a type"
              value={isEmptyString(filters.type) ? undefined : filters.type}
              // Clear `name` when the type changes (see `filtersForTypeChange`): the name filter is
              // contextual to the type, so a carried-over name would silently filter to nothing.
              onChange={(value) => void setFilters(filtersForTypeChange(value ?? ''))}
              options={activityTypeOptions}
              isSearchable
              size="sm"
            />
            {/* Remount the name filter on type change (`key`): resets the free-text input's
                local state and cancels any pending debounce, so a stale name can't be
                reapplied after the type switches (e.g. profile_create → profile_delete). */}
            <NameFilter
              key={filters.type}
              type={filters.type}
              name={filters.name}
              environmentId={environmentId}
              sentEvents={sentEvents}
              sentAttributes={sentAttributes}
              onChange={(name) => void setFilters({ name })}
            />
            <DateRangeFilter from={filters.from} to={filters.to} onChange={(range) => void setFilters(range)} />
            <Button
              variant="secondary"
              size="sm"
              icon="filter"
              isDisabled={!hasFilter}
              onClick={() => void setFilters({ type: '', name: '', from: '', to: '' })}
            >
              Clear Filter
            </Button>
          </>
        )}
      </InlineStack>
      <AutoRefreshControl
        lastUpdated={lastUpdated}
        autoRefresh={autoRefresh}
        onAutoRefreshChange={onAutoRefreshChange}
      />
    </InlineStack>
  );
}
/**
 * Activity Logs auto-refresh: the shared flag-backed toggle state, plus the
 * page-specific behavior of turning refresh off when the user interacts with the
 * list (so it doesn't reload/reorder mid-interaction).
 */
function useAutoRefreshWithInteractionGuard() {
  const { autoRefresh, onAutoRefreshChange } = useAutoRefresh();
  const snackbars = useSnackbars();

  const disableAutoRefreshOnInteraction = useCallback(() => {
    if (!autoRefresh) return;
    onAutoRefreshChange(false);
    snackbars.create({ message: 'Auto refresh was turned off.' });
  }, [autoRefresh, onAutoRefreshChange, snackbars]);

  return { autoRefresh, onAutoRefreshChange, disableAutoRefreshOnInteraction };
}

type ActivityLogTableParams = {
  isAnonymous: boolean;
  workspaceId: string;
  typeTerms: ActivityLogTypeTerms;
  customSmsEnabled: boolean;
  isBuilder: boolean;
  isGeofenceAvailable: boolean;
  isLiveNotificationsAvailable: boolean;
  identifiers: IdentifierConfig;
  segmentById: Map<number, ActivityLogSegment>;
  objectTypeById: Map<number, WorkspaceObjectType>;
  refetch: () => void;
  hasMore: boolean;
  fetchMore: () => void;
  isFetchingMore: boolean;
};

/** Builds the DataTable's columns, expanded-row renderer, load-more config, and type options. */
function useActivityLogTable({
  isAnonymous,
  workspaceId,
  typeTerms,
  customSmsEnabled,
  isBuilder,
  isGeofenceAvailable,
  isLiveNotificationsAvailable,
  identifiers,
  segmentById,
  objectTypeById,
  refetch,
  hasMore,
  fetchMore,
  isFetchingMore,
}: ActivityLogTableParams) {
  const resolveTypeLabel = useCallback((row: ActivityLogRow) => activityLogTypeLabel(row.log, typeTerms), [typeTerms]);
  const activityTypeOptions = useMemo(
    () => buildActivityTypeOptions(typeTerms, { isGeofenceAvailable, isLiveNotificationsAvailable, customSmsEnabled }),
    [typeTerms, isGeofenceAvailable, isLiveNotificationsAvailable, customSmsEnabled],
  );

  const expandedRowComponent = useCallback(
    ({ row }: { row: { original: ActivityLogRow } }) => (
      <ExpandedRow
        row={row.original}
        workspaceId={workspaceId}
        campaignSingular={typeTerms.campaignSingular}
        isAnonymous={isAnonymous}
        identifiers={identifiers}
        segmentById={segmentById}
        onResent={() => refetch()}
      />
    ),
    [workspaceId, typeTerms.campaignSingular, isAnonymous, identifiers, segmentById, refetch],
  );

  const columns = useMemo(
    () =>
      isAnonymous
        ? createAnonymousColumns({ resolveTypeLabel, workspaceId, personSingular: typeTerms.personSingular })
        : createIdentifiedColumns({
            resolveTypeLabel,
            workspaceId,
            personSingular: typeTerms.personSingular,
            objectTypeById,
            isBuilder,
            identifiers,
          }),
    [isAnonymous, resolveTypeLabel, workspaceId, typeTerms.personSingular, objectTypeById, isBuilder, identifiers],
  );

  const loadMore = useMemo(
    () => ({ canLoad: hasMore, onClick: () => fetchMore(), isLoading: isFetchingMore, label: 'Load more' }),
    [hasMore, fetchMore, isFetchingMore],
  );

  return { columns, expandedRowComponent, loadMore, activityTypeOptions };
}

/**
 * Per-tab row expansion, kept above the tab-keyed DataTable so each tab remembers its expanded rows
 * across the remount on tab switch. Newly expanding rows (not collapsing) triggers `onExpand` with
 * their ids — used to stop auto-refresh and to report the expansion to analytics.
 */
function usePerTabRowExpansion(tab: ActivityLogTab, onExpand: (newlyExpandedRowIds: string[]) => void) {
  const [expandedByTab, setExpandedByTab] = useState<Record<ActivityLogTab, Record<string, boolean>>>({
    identified: {},
    anonymous: {},
  });
  // The diff runs against a ref, not the rendered state: two expansions dispatched before React
  // commits would both compare to the same stale map, re-reporting the first row as newly expanded
  // and double-firing its event. `onExpand` can't move into the state updater instead — updaters
  // must stay pure, and StrictMode invokes them twice.
  const expandedByTabRef = useRef(expandedByTab);

  const handleRowExpansionChange = useCallback(
    (next: ExpandedState) => {
      const rows = next === true ? {} : next;
      const previouslyExpanded = expandedByTabRef.current[tab];
      // Collapsing turns up nothing newly expanded, so it neither stops auto-refresh nor counts as
      // a view. The remount on tab switch re-seeds from `initiallyExpandedRows`, which diffs to
      // empty for the same reason — no spurious event for rows the tab already had open.
      const newlyExpanded = Object.keys(rows).filter((id) => rows[id] === true && previouslyExpanded[id] !== true);
      expandedByTabRef.current = { ...expandedByTabRef.current, [tab]: rows };
      setExpandedByTab(expandedByTabRef.current);
      if (newlyExpanded.length > 0) onExpand(newlyExpanded);
    },
    [tab, onExpand],
  );

  return { expandedByTab, handleRowExpansionChange };
}

type TableInteractionsParams = {
  tab: ActivityLogTab;
  rows: ActivityLogRow[];
  tableLoadMore: { canLoad: boolean; onClick: () => void; isLoading: boolean; label: string };
  disableAutoRefreshOnInteraction: () => void;
};

/**
 * The two list interactions that carry side effects: expanding a row and loading more. Both stop
 * auto-refresh (so the list doesn't reorder mid-interaction) and both are reported to analytics.
 * The page view fires from `useActivityLogsAnalytics` here too, so all of the page's analytics
 * enter through one seam.
 */
function useTableInteractions({ tab, rows, tableLoadMore, disableAutoRefreshOnInteraction }: TableInteractionsParams) {
  const { trackMoreLogsLoaded, trackRowsExpanded } = useActivityLogsAnalytics(tab, rows);

  const handleRowExpand = useCallback(
    (rowIds: string[]) => {
      disableAutoRefreshOnInteraction();
      trackRowsExpanded(rowIds);
    },
    [disableAutoRefreshOnInteraction, trackRowsExpanded],
  );
  const { expandedByTab, handleRowExpansionChange } = usePerTabRowExpansion(tab, handleRowExpand);

  // Track first, so the event records the user's intent even when the auto-refresh guard fires its
  // snackbar.
  const loadMore = useMemo(
    () => ({
      ...tableLoadMore,
      onClick: () => {
        trackMoreLogsLoaded();
        disableAutoRefreshOnInteraction();
        tableLoadMore.onClick();
      },
    }),
    [tableLoadMore, disableAutoRefreshOnInteraction, trackMoreLogsLoaded],
  );

  return { expandedByTab, handleRowExpansionChange, loadMore };
}

/**
 * The workspace's object types, keyed by id (resolves object-type names in log rows). Passes
 * `undefined` (disabled) until the `last` alias resolves — `Number('')` is `0`, which the hook's
 * `!= null` guard would treat as a real workspace id and fetch (and cache) workspace 0.
 */
function useObjectTypeById(dataWorkspaceId: string): Map<number, WorkspaceObjectType> {
  const numericId = Number(dataWorkspaceId);
  const query = useListWorkspaceObjectTypes(Number.isInteger(numericId) && numericId > 0 ? numericId : undefined);
  return useMemo(() => new Map((query.data ?? []).map((type) => [type.id, type])), [query.data]);
}

/**
 * The workspace id the data layer should use. The route param may be the `last` alias; the
 * data layer needs the resolved numeric id (its cache keys live under `['workspace', <id>]`
 * for workspace-switch eviction). Links keep using the raw param so alias URLs stay on the
 * alias. Returns '' (disabling the queries) until the workspace resolves.
 */
function useDataWorkspaceId(workspaceId: string): string {
  const currentWorkspace = useCurrentWorkspace();
  const numericId = Number(workspaceId);
  return Number.isInteger(numericId) && numericId > 0 ? workspaceId : String(currentWorkspace?.id ?? '');
}

function ActivityLogsPage({ workspaceId, tab, tabHref }: ActivityLogsPageProps) {
  const isAnonymous = tab === 'anonymous';
  const taxonomy = useProductTaxonomy();
  const auth = useAuth();
  const dataWorkspaceId = useDataWorkspaceId(workspaceId);
  const [filters, setFilters] = useQueryStates(filterParsers, { history: 'replace' });
  const { autoRefresh, onAutoRefreshChange, disableAutoRefreshOnInteraction } = useAutoRefreshWithInteractionGuard();

  const {
    rows,
    segmentById,
    isLoading,
    isError,
    isFetching,
    hasMore,
    fetchMore,
    isFetchingMore,
    lastUpdated,
    refetch,
  } = useLogs({
    environmentId: dataWorkspaceId,
    tab,
    filters: isAnonymous ? ANONYMOUS_FILTERS : filters,
    autoRefresh,
  });

  const typeTerms = useMemo(
    () => ({
      campaignSingular: taxonomy.term('campaigns.titleSingular'),
      personSingular: taxonomy.term('people.titleSingular'),
      personPlural: taxonomy.term('people.titlePlural'),
    }),
    [taxonomy],
  );
  const isBuilder = isBuilderPlan(auth.account);
  const isGeofenceAvailable =
    auth.account != null && hasUnlimitedPlanFeatureAccess(auth.account, PlanFeature.Geofences);
  const isLiveNotificationsAvailable =
    auth.account != null && hasUnlimitedPlanFeatureAccess(auth.account, PlanFeature.LiveNotifications);
  const { sentEvents, sentAttributes, identifiers, customSmsEnabled } = useActivityLogEnvironment(dataWorkspaceId);
  const objectTypeById = useObjectTypeById(dataWorkspaceId);

  const {
    columns,
    expandedRowComponent,
    loadMore: tableLoadMore,
    activityTypeOptions,
  } = useActivityLogTable({
    isAnonymous,
    workspaceId,
    typeTerms,
    customSmsEnabled,
    isBuilder,
    isGeofenceAvailable,
    isLiveNotificationsAvailable,
    identifiers,
    segmentById,
    objectTypeById,
    refetch,
    hasMore,
    fetchMore,
    isFetchingMore,
  });

  // Row expansion (held per tab so it survives the tab-keyed DataTable remount) and load-more,
  // both of which stop auto-refresh and report to analytics.
  const { expandedByTab, handleRowExpansionChange, loadMore } = useTableInteractions({
    tab,
    rows,
    tableLoadMore,
    disableAutoRefreshOnInteraction,
  });

  return (
    <Page flexGrow="1" data-test-activity-logs-page>
      <PageHeader>
        <PageHeaderIconTile name="activity" />
        <PageHeaderTitle>{taxonomy.t('activityLogs.pageTitleWithFormerName')}</PageHeaderTitle>
      </PageHeader>
      <PageBody>
        <TabNav aria-label={taxonomy.t('activityLogs.pageTitle')}>
          <TabNavList>
            <TabNavTab href={tabHref('identified')} isActive={tab === 'identified'}>
              Identified
            </TabNavTab>
            <TabNavTab href={tabHref('anonymous')} isActive={tab === 'anonymous'}>
              Anonymous
            </TabNavTab>
          </TabNavList>
        </TabNav>

        <LogsToolbar
          isAnonymous={isAnonymous}
          environmentId={dataWorkspaceId}
          filters={filters}
          setFilters={setFilters}
          activityTypeOptions={activityTypeOptions}
          sentEvents={sentEvents}
          sentAttributes={sentAttributes}
          lastUpdated={lastUpdated}
          autoRefresh={autoRefresh}
          onAutoRefreshChange={onAutoRefreshChange}
        />

        <DataTable
          // Key by tab: the two tabs have different column sets, and DataTable holds column
          // order in internal (uncontrolled) state. Sharing one instance across tabs lets the
          // Identified order bleed onto Anonymous — `anonymous_id` (absent from that order) gets
          // appended last. A per-tab instance renders each column set in its definition order.
          key={tab}
          caption="Activity log entries"
          data={rows}
          columns={columns}
          getRowId={(row) => row.log.id}
          isLoading={isInitialTableLoad(isLoading, isFetching, rows.length)}
          // A failed fetch shows the error state in the same (empty) slot as "no data". The table
          // only renders it when there are no rows, so a mid-pagination error keeps the loaded rows.
          emptyStateComponent={emptyStateForFetch(isError)}
          layout="fixed"
          withSorting={false}
          withRowExpanding
          getRowCanExpand={() => true}
          expandedRowComponent={expandedRowComponent}
          initiallyExpandedRows={expandedByTab[tab]}
          onRowExpansionChange={handleRowExpansionChange}
          loadMore={loadMore}
        />
      </PageBody>
    </Page>
  );
}

export default ActivityLogsPage;
